import {
  Chip,
  Divider,
  Paper,
  SnackbarContent,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Box, Stack } from "@mui/system";
import Card from "@mui/material/Card";
import DataStatusAlert from "../../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { formatDate } from '../../../../../../utils/helpers';

const InsightCards = () => {
  const [searchParams] = useSearchParams();
  const taskDetails = useSelector(
    (state: any) => state.businessInsights.taskDetails
  );
  const taskStructureUid = searchParams.get("taskStructureUid");

  const ADGROUP_TASK_UIDS = [
    "39caae988f684bbca8aa8c6fd7924ecf",
    "dcb12de5b0e54e47af190902e42fac09",
  ];
  const CAMPAIGN_TASK_UIDS = [
    "210190e5ca7b4e29a83e8537b4c92d4e",
    "4bfc5e1b714c4b88b186f2f4042244e8",
  ];
  const CAC_30_TASK_UIDS = [
    "39caae988f684bbca8aa8c6fd7924ecf",
    "210190e5ca7b4e29a83e8537b4c92d4e",
  ];

  const formatDates = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const generateFlags = (details: any) => {
    const flags = [];

    // Check FTD
    if (details?.data?.["query_output:FTD_30"]?.value < 30) {
      flags.push({ message: "FTD < 30", type: "error" });
    }

    // Check QFTD
    if (details?.data?.["query_output:QFTD_30"]?.value < 30) {
      flags.push({ message: "QFTD < 30", type: "error" });
    }

    // Check Clickout/Clickin ratio
    if (details?.data?.["query_output"]?.diff <= -0.3) {
      const decreasePercentage = Math.abs(
        details.data["query_output"].diff * 100
      ).toFixed(2);
      flags.push({
        message: `Clickout/Clickin ratio decrease of ${decreasePercentage}%`,
        type: "error",
      });
    }

    // If no flags, show all clear message
    if (flags.length === 0) {
      flags.push({ message: "All Clear! No Red Flags", type: "success" });
    }
    return flags;
  };

  const getCardData = () => {
    const cards = [
      {
        title: "CAC",
        value: CAC_30_TASK_UIDS.includes(taskStructureUid || "")
          ? `${Number(taskDetails["data"]["query_output:CAC_30"]["value"]).toFixed(2)}$`
          : `${Number(taskDetails["data"]["query_output:CAC_2_days_ago"]["value"]).toFixed(2)}$`,
        trend: {
          percentage: CAC_30_TASK_UIDS.includes(taskStructureUid || "")
            ? `${Math.round(taskDetails["data"]["query_output:delta_CAC_tcpa_30_day"]["value"] * 100)}%`
            : `${Math.round(taskDetails["data"]["query_output:delta_CAC_tcpa_2_day"]["value"] * 100)}%`,
          label: "Gap since change."
        }
      },
      {
        title: "TCPA",
        value: `${Number(taskDetails["data"]["query_output:new_tcpa"]["value"])}$`,
        trend: null
      },
      {
        title: "Bid Change",
        value: formatDates(taskDetails["data"]["query_output:last_price_change"]["value"]),
        trend: {
          percentage: `${taskDetails["data"]["query_output:num_of_days_since_change"]["value"]}`,
          label: "Days since Bid Change"
        }
      },
      {
        title: "FTD",
        value: taskDetails["data"]["query_output:FTD_30"]["value"],
        trend: null
      },
      {
        title: "QFTD",
        value: taskDetails["data"]["query_output:QFTD_30"]["value"],
        trend: null
      },
      {
        title: "COST",
        value: `${Math.round(Number(taskDetails["data"]["query_output:cost"]["value"]))}$`,
        trend: null
      },
      {
        title: "CLICKS",
        value: taskDetails["data"]["query_output:clicks"]["value"],
        trend: null
      }
    ];
    return cards;
  };

  return (
    <>
      <Box>
        <Paper sx={{ padding: "8px 16px 16px" }}>
          <Grid container>
            <Grid size={12}>
              <Box>
                {/* Overview Section */}
                <Stack spacing={1}>
                  {/* Title Row with Chips */}
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={2}
                  >
                    <Typography fontSize={24} fontWeight={400}>
                      {taskDetails["data"]["query_output:sec_title"]["value"]}
                    </Typography>
                    <Typography fontSize={24} fontWeight={400}>
                      {formatDates(
                        taskDetails["data"]["query_output:start_date"]["value"]
                      )}{" "}
                      -{" "}
                      {formatDates(
                        taskDetails["data"]["query_output:end_date"]["value"]
                      )}
                    </Typography>
                    <Stack direction="row" gap={2} alignItems="center">
                      <Chip
                        label={
                          taskDetails["data"]["query_output:traffic_source"][
                            "value"
                          ]
                        }
                        sx={{
                          backgroundColor: "#7B1FA2",
                          color: "white",
                          height: "26px",
                        }}
                      />
                      <DataStatusAlert status={taskDetails["current_status"]} />
                    </Stack>
                    <Typography fontSize={14}>
                      Created On:{"  "}
                      {formatDate(taskDetails["created_at"])}
                    </Typography>
                  </Stack>
                  {/* Metadata Row */}
                  <Stack direction="row" gap={1} mt={0.5}>
                    {ADGROUP_TASK_UIDS.includes(taskStructureUid || "") && (
                      <>
                        <Typography fontSize={16} mr={1}>
                          Adgroup Name:{" "}
                          <Typography component="span" fontWeight={700}>
                            {taskDetails["data"]["query_output:adgroup_name"][
                              "value"
                              ]
                            }
                          </Typography>
                        </Typography>
                        <Typography fontSize={16}>
                          Adgroup ID:{" "}
                          <Typography component="span" fontWeight={700}>
                            {
                              taskDetails["data"]["query_output:adgroup_id"][
                              "value"
                              ]
                            }
                          </Typography>
                        </Typography>
                        <Typography fontSize={16}>
                          Campaign Name:{" "}
                          <Typography component="span" fontWeight={700}>
                            {taskDetails["data"]["query_output:campaign_name"]["value"]}
                          </Typography>
                        </Typography>
                      </>
                    )}
                    {CAMPAIGN_TASK_UIDS.includes(taskStructureUid || "") && (
                      <>
                        <Typography fontSize={16}>
                          Campaign Name:{" "}
                          <Typography component="span" fontWeight={700}>
                            {taskDetails["data"]["query_output:campaign_name"]["value"]}
                          </Typography>
                        </Typography>
                        <Typography fontSize={16}>
                          Campaign ID:{" "}
                          <Typography component="span" fontWeight={700}>
                            {taskDetails["data"]["query_output:campaign_id"]["value"]}
                          </Typography>
                        </Typography>
                      </>
                    )}
                  </Stack>
                  {/* Description */}
                  <Divider sx={{ margin: "8px 0" }} />
                  <Typography fontSize={14} color="text.secondary" mt={0.5}>
                    {taskDetails["data"]["meta_data:description"]}
                  </Typography>
                </Stack>
                {/* Analysis Section */}
                <Stack direction={"row"} gap={2} mt={1.5}>
                  {getCardData().map((card, index) => (
                    <Card
                      key={index}
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #E0E0E0",
                        borderRadius: "8px",
                        width: "100%",
                        // maxHeight: "98px", 
                      }}
                    >
                      <Box sx={{ padding: "8px !important" }}>
                        <Typography
                          sx={{ fontWeight: "500", fontSize: "14px" }}
                          color="text.secondary"
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "500",
                            fontSize: "20px",
                            textAlign: "center",
                            marginTop: "8px",
                            color: "#000000DE",
                          }}
                        >
                          {card.value}
                        </Typography>
                        {card.trend && (
                          <Stack
                            direction={"column"}
                            alignItems={"center"}
                            gap={0.5}
                            sx={{ marginTop: "8px" }}
                            justifyContent={"center"}
                          >
                            <Stack direction={"row"} alignItems={"center"} gap={0.5} justifyContent={"center"}>
                              <TrendingUpIcon
                              sx={{
                                color: "#D32F2F",
                                width: "14px",
                                height: "14px",
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: "500",
                                fontSize: "10px",
                                textAlign: "center",
                                color: "#D32F2F",
                              }}
                            >
                              {card.trend.percentage}
                            </Typography>
                            </Stack>
                            <Typography
                              sx={{
                                fontWeight: "500",
                                fontSize: "10px",
                                color: "#000000DE",
                              }}
                            >
                              {card.trend.label}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Card>
                  ))}
                </Stack>
              </Box>
              <Divider sx={{ margin: "16px 0" }} />
            </Grid>
            {/* Red Flag Section */}
            <Grid size={12}>
              <Box>
                <Stack
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  sx={{ width: "100%", paddingBottom: "16px" }}
                >
                  <Typography sx={{ fontSize: "18px" }}>Red Flags!</Typography>
                </Stack>
                <Stack
                  sx={{
                    overflowX: "auto",
                    maxWidth: "100%",
                    gap: 2,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                  }}
                >
                  {generateFlags(taskDetails).map((item, index) => (
                    <SnackbarContent
                      key={index}
                      sx={{
                        backgroundColor:
                          item.type === "error" ? "#FDEDED" : "#EDF7ED",
                        color: item.type === "error" ? "#5F2120" : "#1E4620",
                        boxShadow: "none",
                        border:
                          item.type === "error"
                            ? "1px solid #D32F2F80"
                            : "1px solid #2E7D3280",
                        padding: "0 !important",
                        minWidth: "fit-content",
                        "& .MuiSnackbarContent-message": {
                          padding: "2px 0",
                        },
                      }}
                      message={
                        <Box display="flex">
                          {item.type === "error" ? (
                            <ErrorOutlineIcon
                              sx={{
                                marginRight: "12px",
                                marginLeft: "16px",
                                color: "#D32F2F",
                              }}
                            />
                          ) : (
                            <CheckCircleOutlineOutlinedIcon
                              sx={{
                                marginRight: "12px",
                                marginLeft: "16px",
                                color: "#2E7D32",
                              }}
                            />
                          )}
                          <Box>
                            <Typography variant="body1" fontWeight="500">
                              {item.message}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default InsightCards;
